import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles } from 'lucide-react';
import api from '../../services/api';

/**
 * Cashout Rules Popup (Iter 142.10)
 * =================================
 * Player-facing modal that renders admin-edited cashout rules with big
 * fonts, generous spacing, and a clean visual hierarchy.
 *
 * Rules content is fetched from:
 *   - /api/cashout-rules/{gameId}  →  per-game override if set
 *   - /api/cashout-rules           →  global fallback
 *
 * Both endpoints return the same shape: { headline, intro, rules[], footer }.
 *
 * The popup is portal-mounted so it sits ABOVE the parent game-detail
 * card and can size to the viewport on mobile.
 */
export default function CashoutRulesPopup({ gameId, onClose, overrideDoc = null }) {
  const [doc, setDoc] = useState(overrideDoc);
  const [loading, setLoading] = useState(!overrideDoc);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (overrideDoc) {
      // Inline preview mode — admin passes the unsaved doc directly.
      setDoc(overrideDoc);
      setLoading(false);
      return undefined;
    }
    let live = true;
    setLoading(true);
    const url = gameId ? `/api/cashout-rules/${gameId}` : '/api/cashout-rules';
    api
      .get(url)
      .then((r) => { if (live) setDoc(r.data); })
      .catch((e) => {
        if (live) setErr(e.response?.data?.message || 'Could not load cashout rules.');
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [gameId, overrideDoc]);

  // Lock scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Esc to close.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const overlay = (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
      data-testid="cashout-rules-overlay"
    >
      <div
        className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl"
        style={{
          background: 'linear-gradient(180deg, #14181f 0%, #0B0E14 100%)',
          border: '1px solid rgba(212,175,55,0.25)',
          boxShadow: '0 -20px 60px rgba(212,175,55,0.15), 0 20px 60px rgba(0,0,0,0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="cashout-rules-popup"
      >
        {/* Sticky header */}
        <div
          className="sticky top-0 z-10 flex items-start justify-between gap-3 px-6 pt-6 pb-4"
          style={{
            background: 'linear-gradient(180deg, rgba(20,24,31,1) 60%, rgba(20,24,31,0.7) 100%)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div className="flex items-start gap-3">
            <div
              className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #D4AF37, #F5D76E)',
                boxShadow: '0 6px 24px rgba(212,175,55,0.35)',
              }}
            >
              <Sparkles className="w-6 h-6 text-black" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300/70 font-semibold">
                Cashout Rules
              </p>
              <h2
                className="text-2xl sm:text-3xl font-black text-white leading-tight mt-0.5"
                data-testid="cashout-rules-headline"
              >
                {doc?.headline || 'Cashout Rules'}
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-2 rounded-full hover:bg-white/10 transition"
            data-testid="cashout-rules-close"
            aria-label="Close cashout rules"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-8">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="spinner w-7 h-7" />
            </div>
          )}
          {err && !loading && (
            <p className="text-red-400 text-base py-6 text-center">{err}</p>
          )}
          {!loading && !err && doc && (
            <>
              {doc.intro && (
                <p
                  className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6"
                  data-testid="cashout-rules-intro"
                >
                  {doc.intro}
                </p>
              )}

              <ul className="space-y-3">
                {(doc.rules || []).map((r, idx) => (
                  <li
                    key={idx}
                    className="rounded-2xl p-4 sm:p-5"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                    data-testid={`cashout-rule-${idx}`}
                  >
                    <div className="flex items-start gap-3 sm:gap-4">
                      <div
                        className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center text-2xl"
                        style={{
                          background: 'rgba(212,175,55,0.12)',
                          border: '1px solid rgba(212,175,55,0.25)',
                        }}
                      >
                        {r.icon || '•'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg sm:text-xl font-bold text-white leading-snug mb-1.5">
                          {r.title}
                        </h3>
                        <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                          {r.body}
                        </p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {doc.footer && (
                <p
                  className="text-sm text-gray-400 italic leading-relaxed mt-6 text-center"
                  data-testid="cashout-rules-footer"
                >
                  {doc.footer}
                </p>
              )}
            </>
          )}
        </div>

        {/* Sticky footer CTA */}
        <div
          className="sticky bottom-0 px-6 py-4 border-t"
          style={{
            background: 'rgba(20,24,31,0.95)',
            borderColor: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <button
            onClick={onClose}
            className="w-full py-4 rounded-2xl text-base font-bold transition-all hover:scale-[1.02]"
            style={{
              background: 'linear-gradient(135deg, #D4AF37, #F5D76E)',
              color: '#000',
              boxShadow: '0 8px 24px rgba(212,175,55,0.25)',
            }}
            data-testid="cashout-rules-acknowledge"
          >
            Got it, let&apos;s play
          </button>
        </div>
      </div>
    </div>
  );

  const host = typeof document !== 'undefined' ? document.body : null;
  return host ? createPortal(overlay, host) : overlay;
}
